import { cloneDeep, debounce, isEmpty } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'reactstrap';
import { getGLTemplateId } from '../../../api/glTemplate';
import {
  getCancelReasonList,
  getGLConfigData,
  getGLConfigEvents,
  updateGLConfigData,
} from '../../../api/journalGLConfig';
import { fetchCommonFilters } from '../../../api/reports';
import EmptyEvent from '../../../assets/images/j-gl.svg';
import { Button, InfoLabel, Layout, Select, Table } from '../../../common/components';
import { useSetLoading } from '../../../common/hooks/appearance';
import { apiErrorHandler } from '../../../utils/handlers';
import { asyncDebounce } from '../../../utils/utils';
import ConfirmPopup from './ConfirmPopup';
import SuccessPopup from './SuccessPopup';

const JournalGlconfig = () => {
  const [loading, setLoading] = useState(false);
  const [templateDropdownOptions, setTemplateDropdownOptions] = useState([]);
  const [eventDropdownOptions, setEventDropdownOptions] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [tempSelectedEvent, setTempSelectedEvent] = useState(null); // used for temporarly saving event selected, used after confirm popup actions
  const [tempSelectedTeam, setTempSelectedTeam] = useState(null);
  const [tempSelectedCancelReason, setTempSelectedCancelReason] = useState(null);
  const [tableData, setTableData] = useState({});
  const [showSaveConfirmPopup, setShowSaveConfirmPopup] = useState(false);
  const [showSaveSuccessPopup, setShowSaveSuccessPopup] = useState(false);
  const [teamDropdownOptions, setTeamDropDownOptions] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  // setTeamDropDownOptions({ value: 1, label: 'Team1' });
  const [cancelEvents, setCancelEvents] = useState([]);
  const [cancelReasonDropdownOptions, setCancelReasonDropdownOptions] = useState([]);
  const [selectedCancelReason, setSelectedCancelReason] = useState(null);
  const [conditionalReason, setConditionalReason] = useState(null);

  const [showEventChangeConfirmPopup, setShowEventChangeConfirmPopup] = useState(false);
  const [changedData, setChangedData] = useState({}); // used for saving template values from onChange handler

  const [tableLeftScopllPos, setTableLeftScopllPos] = useState(0);

  // conditional reasons  for some cancellation
  const conditionalEvents = [
    'Asset to Equity on Cancelled Contract',
    'Equity on Cancelled Contract to Other Income',
  ];

  const toggleSaveConfirmPopup = () => setShowSaveConfirmPopup((s) => !s);
  const toggleEventChangeConfirmPopup = () => setShowEventChangeConfirmPopup((s) => !s);
  const toggleSaveSuccessPopup = () => setShowSaveSuccessPopup((s) => !s);

  const fetchEventList = async () => {
    setLoading(true);
    try {
      const response = await getGLConfigEvents();
      const events = [];
      setLoading(false);
      const formatedResp = response?.data?.data?.map((d) => {
        if (d.isCancelled) {
          events.push(d.name);
        }
        return {
          value: d.id,
          label: d.name,
        };
      });
      setCancelEvents(events);
      setEventDropdownOptions(formatedResp);
      return formatedResp;
    } catch (err) {
      setLoading(false);
      apiErrorHandler(err);
    }
  };

  const fetchTeamsList = async () => {
    setLoading(true);
    try {
      const teamsResponse = await fetchCommonFilters({ type: 'team' });
      setLoading(false);
      const formatedTeamResponse = teamsResponse?.data?.data?.teams?.map((eachTeam) => ({
        value: eachTeam.id,
        label: eachTeam.name,
      }));
      setSelectedTeam(formatedTeamResponse[0]);
      setTeamDropDownOptions(formatedTeamResponse);
      return formatedTeamResponse;
    } catch (err) {
      setLoading(false);
      apiErrorHandler(err);
    }
  };

  const fetchCancelReasonList = async () => {
    setLoading(true);
    try {
      const cancelReasonResp = await getCancelReasonList();
      const formatedReasons = cancelReasonResp?.data?.data?.map((reason) => ({
        value: reason.id,
        label: reason.reason,
      }));
      setSelectedCancelReason(formatedReasons[0]);
      setCancelReasonDropdownOptions(formatedReasons);
      const filtering = [];
      for (let i = 0; i < 5; i++) {
        if (!['Buy Back', 'ICCFA Transfer'].includes(formatedReasons[i].label)) {
          filtering.push(formatedReasons[i]);
        }
      }
      setConditionalReason(filtering);
    } catch (error) {
      setLoading(false);
      apiErrorHandler(error);
    }
  };
  const fetchGLConfiguration = async (eventId, teamId, reasonId) => {
    setLoading(true);
    try {
      const response = await getGLConfigData({
        glEventId: eventId,
        teamId,
        reasonId,
      });
      setLoading(false);
      const d = cloneDeep(response?.data?.data);
      // mutate the mappings array to match the locations
      // this is done to help rendering of dropdowns
      d.data.forEach((et) => {
        d.headers.forEach((h) => {
          if (et.mapping.find((m) => m.locationId === h.locationId)) return;
          et.mapping.push({
            locationId: h.locationId,
            locationName: h.locationName,
            isActive: 1,
          });
        });
      });
      setTableData(d);

      // this logic is for adding the GL Templates from GLCongig fetched in above API call to templateDropdownOptions
      // this is requied because initally we are only fetching 15 GL Templates for dropdowns
      // and the template from above API might not be available in that 15 items
      const tempObj = {};
      d.data.forEach((et) => {
        et.mapping.forEach((m) => {
          if (m.templateId && m.templateIdentificationNumber) {
            tempObj[m.templateId] = {
              value: m.templateId,
              label: m.templateIdentificationNumber,
            };
          }
        });
      });

      // for removing duplicate options
      templateDropdownOptions.forEach((to) => {
        if (to.value && to.label) tempObj[to.value] = to;
      });
      const templateDropdownOptionsFromResp = Object.values(tempObj);
      setTemplateDropdownOptions(templateDropdownOptionsFromResp);
    } catch (err) {
      setLoading(false);
      apiErrorHandler(err);
    }
  };

  const fetchGLTemplateList = async (searchVal, action = 'default') => {
    setLoading(true);
    try {
      const params = {
        searchValue: searchVal || '',
        page: 1,
        limit: 15,
      };
      const response = await getGLTemplateId(params);
      setLoading(false);
      const formatedResp = response?.data?.data?.map((d) => ({
        value: d.templateId,
        label: d.identificationNumber,
      }));

      // not saving the search results to the templateDropdownOptions
      if (action === 'default') setTemplateDropdownOptions(formatedResp);

      return formatedResp; // this is required for search
    } catch (err) {
      setLoading(false);
      apiErrorHandler(err);
    }
  };

  useEffect(() => {
    fetchEventList();
    fetchTeamsList();
    fetchCancelReasonList();
    fetchGLTemplateList('', 'default');
  }, []);

  const handleGLTemplateChange = (glTemplate, eventTypeId, locationId, ogDropdownValue) => {
    const temp = cloneDeep(changedData);
    const dataKey = `${eventTypeId}-${locationId}`;

    // if template already selected / saved don't do anythig
    // if template is same as original and it is present in changedData obj remove it
    if (ogDropdownValue.value === glTemplate.value) {
      delete temp[dataKey];
      setChangedData(temp);
    } else {
      // if original tempalte and selected template are different then add it to changedData obj
      // save the changed template to changedData for later use when calling API
      temp[dataKey] = {
        GLEventTypeId: eventTypeId,
        templateId: glTemplate.value,
        locationId,
        templateData: glTemplate, // used for displaying selected value
      };
      if (['Commission', 'Commission Cancellation'].includes(selectedEvent?.label)) {
        temp[dataKey].teamId = selectedTeam.value;
      }
      if (cancelEvents.includes(selectedEvent?.label)) {
        temp[dataKey].cancelledReasonId = selectedCancelReason?.value;
      }
      setChangedData(temp);

      // when a new template is selected from after searching we add it to the templateDropdownOptions for display purpose
      const tempObj = {};
      tempObj[glTemplate.value] = glTemplate;

      // for removing duplicate options
      templateDropdownOptions.forEach((to) => {
        if (to.value && to.label) tempObj[to.value] = to;
      });
      const templateDropdownOptionsFromResp = Object.values(tempObj);
      setTemplateDropdownOptions(templateDropdownOptionsFromResp);
    }
  };

  const toggleConfirmPopup = () => setShowSaveConfirmPopup((s) => !s);

  const handleSaveGLConfigs = () => {
    toggleConfirmPopup();
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // remove the templateData field which we added for render purpose from request data
      const changeArray = Object.values(changedData).map((d) => {
        const temp = d;
        delete temp?.templateData;
        return temp;
      });
      await updateGLConfigData({ data: changeArray });
      toggleSaveSuccessPopup();
      if (['Commission', 'Commission Cancellation'].includes(selectedEvent?.label)) {
        if (cancelEvents.includes(selectedEvent?.label)) {
          fetchGLConfiguration(
            selectedEvent?.value,
            selectedTeam.value,
            selectedEvent?.label === 'Commission' ? null : selectedCancelReason?.value,
            // selectedCancelReason?.value
          );
        } else {
          fetchGLConfiguration(selectedEvent?.value, selectedTeam?.value);
        }
      } else if (cancelEvents.includes(selectedEvent?.label)) {
        fetchGLConfiguration(selectedEvent?.value, null, selectedCancelReason?.value);
      } else {
        fetchGLConfiguration(selectedEvent?.value);
      }
      toggleConfirmPopup();
      setChangedData({});
      setLoading(false);
    } catch (err) {
      setLoading(false);
      apiErrorHandler(err);
    }
  };

  const debounceApiCall = asyncDebounce(fetchGLTemplateList, 1000);

  const handleEventChange = (event) => {
    if (!isEmpty(changedData)) {
      setTempSelectedEvent(event);
      if (event?.label === 'Commission' || event?.label === 'Commission Cancellation') {
        setTempSelectedTeam(teamDropdownOptions[0]);
      }
      if (cancelEvents.includes(event?.label)) {
        setTempSelectedCancelReason(cancelReasonDropdownOptions[0]);
      }
      toggleEventChangeConfirmPopup();
    } else {
      setSelectedEvent(event);
      setSelectedTeam(teamDropdownOptions[0]);
      setSelectedCancelReason(cancelReasonDropdownOptions[0]);
      if (event?.label === 'Commission' || event?.label === 'Commission Cancellation') {
        if (cancelEvents.includes(event?.label)) {
          fetchGLConfiguration(
            event?.value,
            teamDropdownOptions[0].value,
            event?.label === 'Commission Cancellation'
              ? cancelReasonDropdownOptions[0]?.value
              : null,
          );
        } else {
          fetchGLConfiguration(event?.value, teamDropdownOptions[0].value);
        }
      } else if (cancelEvents.includes(event?.label)) {
        fetchGLConfiguration(event?.value, null, cancelReasonDropdownOptions[0]?.value);
      } else {
        fetchGLConfiguration(event?.value);
      }
    }
  };
  const handleTeamChange = (event) => {
    if (!isEmpty(changedData)) {
      setTempSelectedEvent(selectedEvent);
      setTempSelectedTeam(event);
      if (cancelEvents.includes(event?.label)) {
        setTempSelectedCancelReason(cancelReasonDropdownOptions[0]);
      }
      toggleEventChangeConfirmPopup();
    } else {
      setSelectedTeam(event);
      setSelectedCancelReason(cancelReasonDropdownOptions[0]);
      if (cancelEvents.includes(selectedEvent?.label)) {
        fetchGLConfiguration(
          selectedEvent?.value,
          event?.value,
          selectedEvent?.label === 'Commission Cancellation'
            ? cancelReasonDropdownOptions[0]?.value
            : null,
        );
      } else {
        fetchGLConfiguration(selectedEvent?.value, event?.value);
      }
    }
  };

  const handleCancelReason = (event) => {
    if (!isEmpty(changedData)) {
      setTempSelectedEvent(selectedEvent);
      if (
        selectedEvent?.label === 'Commission' ||
        selectedEvent?.label === 'Commission Cancellation'
      ) {
        setTempSelectedTeam(selectedTeam);
      }
      setTempSelectedCancelReason(event);
      toggleEventChangeConfirmPopup();
    } else {
      setSelectedCancelReason(event);
      if (
        selectedEvent?.label === 'Commission' ||
        selectedEvent?.label === 'Commission Cancellation'
      ) {
        fetchGLConfiguration(
          selectedEvent?.value,
          selectedTeam?.value,
          selectedEvent?.label === 'Commission Cancellation' ? event?.value : null,
          // event?.value
        );
      } else {
        fetchGLConfiguration(selectedEvent?.value, null, event?.value);
      }
    }
  };
  const handleCancelGLConfigs = () => setChangedData({});
  const handleEventChangeCancel = () => {
    setTempSelectedEvent(null);
    setTempSelectedTeam(null);
    setTempSelectedCancelReason(null);
    toggleEventChangeConfirmPopup();
  };
  const handleEventChangeConfirm = () => {
    setSelectedEvent(tempSelectedEvent);
    if (
      tempSelectedEvent?.label === 'Commission' ||
      tempSelectedEvent?.label === 'Commission Cancellation'
    ) {
      setSelectedTeam(tempSelectedTeam);
      if (cancelEvents.includes(tempSelectedEvent?.label)) {
        const reasonTemp = tempSelectedCancelReason || cancelReasonDropdownOptions[0];
        setSelectedCancelReason(reasonTemp);
        fetchGLConfiguration(
          tempSelectedEvent?.value,
          tempSelectedTeam?.value,
          tempSelectedEvent?.label === 'Commission Cancellation' ? reasonTemp?.value : null,
          // reasonTemp?.value
        );
      } else {
        fetchGLConfiguration(
          tempSelectedEvent?.value,
          tempSelectedTeam?.value,
          tempSelectedEvent?.label === 'Commission Cancellation'
            ? cancelReasonDropdownOptions[0]?.value
            : null,
          // cancelReasonDropdownOptions[0]?.value
        );
      }
    } else if (cancelEvents.includes(tempSelectedEvent?.label)) {
      setSelectedCancelReason(tempSelectedCancelReason);
      fetchGLConfiguration(
        tempSelectedEvent?.value,
        tempSelectedTeam?.value,
        tempSelectedCancelReason?.value,
      );
    } else {
      fetchGLConfiguration(
        tempSelectedEvent?.value,
        tempSelectedTeam?.value,
        tempSelectedCancelReason?.value,
      );
    }
    toggleEventChangeConfirmPopup();
    // reset states
    setChangedData({});
    setTempSelectedEvent(null);
    setTempSelectedTeam(null);
    setTempSelectedCancelReason(null);
  };
  const getDropdownValue = (eventTypeId, locationId) => {
    // using the value if present in changedData variable locally
    const dataKey = `${eventTypeId}-${locationId}`;
    const changeExists = changedData[dataKey];
    if (changeExists) {
      return {
        label: changeExists.templateData?.label,
        value: changeExists.templateId,
      };
    }
    return null;
  };

  const tableScrollHandler = () =>
    setTableLeftScopllPos(document.getElementById('gl-config-table')?.scrollLeft || 0);

  const debouncedTableScrollHandler = useMemo(
    () => debounce(tableScrollHandler, 300),
    [tableScrollHandler],
  );

  // Stop the invocation of the debounced function
  // after unmounting
  useEffect(() => {
    return () => debouncedTableScrollHandler.cancel();
  }, [debouncedTableScrollHandler.cancel]);

  const tableHeaders = tableData?.headers?.map((item) => item?.locationName) || [];

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);
  return (
    <Layout>
      <div className="layout-in-div">
        <div className="j-gl-heading mt-5">
          <InfoLabel showIcon label="GL Configuration Screen" />
        </div>
        <hr />
        <div className="j-gl-section mt-5">
          {/* <div className="rowdiv"> */}
          <Row>
            <Col className="col-4">
              <h6 className="j-gl-h6">EVENTS</h6>
              <div className="j-gl-dropdown">
                <Select
                  classNamePrefix="select-dropdown"
                  size="lg"
                  options={eventDropdownOptions}
                  value={selectedEvent}
                  onChange={handleEventChange}
                  placeholder="Select Event"
                  name="select_event"
                />
              </div>
            </Col>
            {(selectedEvent && selectedEvent?.label === 'Commission') ||
            selectedEvent?.label === 'Commission Cancellation' ? (
              <Col className="col-4">
                <h6 className="j-gl-h6">TEAMS</h6>
                <div className="j-gl-dropdown-teams">
                  <Select
                    classNamePrefix="select-dropdown"
                    size="lg"
                    options={teamDropdownOptions}
                    value={selectedTeam}
                    onChange={handleTeamChange}
                    placeholder="Select Team"
                    name="select_team"
                  />
                </div>
              </Col>
            ) : null}
            {selectedEvent && cancelEvents.includes(selectedEvent?.label) ? (
              <Col className="col-4">
                <h6 className="j-gl-h6">REASON FOR CANCELLATION</h6>
                <div className="j-gl-dropdown-teams">
                  <Select
                    classNamePrefix="select-dropdown"
                    size="lg"
                    options={
                      conditionalEvents.includes(selectedEvent?.label)
                        ? conditionalReason
                        : cancelReasonDropdownOptions
                    }
                    value={selectedCancelReason}
                    onChange={handleCancelReason}
                    placeholder="Select Cancel reason"
                    name="select_cancel_reason"
                  />
                </div>
              </Col>
            ) : null}
          </Row>
          {/* </div> */}

          <div className="text-center">
            {selectedEvent && tableData?.data?.length > 0 ? (
              <>
                <Row>
                  <Col>
                    <div className="segment-btns mb-4">
                      <Button
                        color="default"
                        className="j-btn-1 j-btn-2"
                        size="xs1"
                        type="button"
                        onClick={handleCancelGLConfigs}
                      >
                        Cancel
                      </Button>
                      <Button
                        color="primary"
                        className="j-btn-1"
                        size="xs1"
                        type="button"
                        onClick={handleSaveGLConfigs}
                        disabled={isEmpty(changedData)}
                      >
                        Save
                      </Button>
                    </div>
                  </Col>
                </Row>
                <Table
                  headers={[selectedEvent?.label, ...tableHeaders]}
                  bordered
                  responsive
                  className="dashboard-table j-gl-table journal-gl-table"
                  id="gl-config-table"
                  onScroll={debouncedTableScrollHandler}
                >
                  <tbody>
                    {tableData?.data?.map((eventType) => (
                      <tr key={eventType?.GLEventTypeId} className="table-body-tr gl-j-tr">
                        <td className="text-center vertical-middle j-gl-main-td">
                          {eventType?.GLEventTypeName}
                        </td>
                        {tableData?.headers.map((locationItem, idx) => (
                          <td className="text-center" key={locationItem?.locationId}>
                            {eventType.mapping.map((mappingItem) => {
                              const originalDropdownValue =
                                mappingItem?.locationId === locationItem?.locationId
                                  ? {
                                      label: mappingItem?.templateIdentificationNumber,
                                      value: mappingItem?.templateId,
                                    }
                                  : null;
                              const changedDropdownValue = getDropdownValue(
                                eventType?.GLEventTypeId,
                                mappingItem?.locationId,
                              );

                              // if column is scrolled and some part is hidden behind fixed column
                              // disable the select dropdown, which will break ui
                              let disableSelect = false;
                              // each column has 300 fixed width
                              // if > 10% of column is scrolled left then disable the column
                              if (tableLeftScopllPos % 300 > 40) {
                                disableSelect = Math.floor(tableLeftScopllPos / 300) === idx;
                              }

                              return (
                                mappingItem?.locationId === locationItem?.locationId && (
                                  <div
                                    className="common-local-search w-100"
                                    key={`${eventType.GLEventTypeId}-${mappingItem?.locationId}`}
                                  >
                                    <i className="fas fa-search" />
                                    <Select
                                      name={`${eventType.GLEventTypeId}-${mappingItem?.locationId}`}
                                      size="lg"
                                      placeholder=""
                                      value={changedDropdownValue || originalDropdownValue}
                                      cacheOptions
                                      isAsync
                                      loadOptions={debounceApiCall}
                                      defaultOptions={templateDropdownOptions}
                                      disabled={disableSelect || !mappingItem?.isActive}
                                      onChange={(template) =>
                                        handleGLTemplateChange(
                                          template,
                                          eventType?.GLEventTypeId,
                                          mappingItem?.locationId,
                                          originalDropdownValue,
                                        )
                                      }
                                      menuPortalTarget={document.getElementById(
                                        'select-dropdown-portal',
                                      )}
                                      closeMenuOnScroll={(e) =>
                                        e?.target?.classList?.contains('j-gl-table')
                                      }
                                    />
                                  </div>
                                )
                              );
                            })}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </Table>
                <Row>
                  <Col>
                    <div className="segment-btns">
                      <Button
                        color="default"
                        className="j-btn-1 j-btn-2"
                        size="xs1"
                        type="button"
                        onClick={handleCancelGLConfigs}
                      >
                        Cancel
                      </Button>
                      <Button
                        color="primary"
                        className="j-btn-1"
                        size="xs1"
                        type="button"
                        onClick={handleSaveGLConfigs}
                        disabled={isEmpty(changedData)}
                      >
                        Save
                      </Button>
                    </div>
                  </Col>
                </Row>
              </>
            ) : (
              <img src={EmptyEvent} alt="icon" className="j-gl-img-no-events" />
            )}
          </div>
        </div>
        <ConfirmPopup
          isOpen={showSaveConfirmPopup}
          toggle={toggleSaveConfirmPopup}
          onConfirm={handleSave}
          message="Do you really want to save the changes ?"
          confirmBtnText="CONFIRM"
          cancelBtnText="CANCEL"
        />
        <ConfirmPopup
          isOpen={showEventChangeConfirmPopup}
          toggle={handleEventChangeCancel}
          onConfirm={handleEventChangeConfirm}
          message="Do you want to discard the changes?"
          confirmBtnText="Yes"
          cancelBtnText="No"
        />
        <SuccessPopup isOpen={showSaveSuccessPopup} toggle={toggleSaveSuccessPopup} />
        {/* Reference: https://stackoverflow.com/a/58660122 */}
        <div id="select-dropdown-portal" className="select-dropdown-portal" />
      </div>
    </Layout>
  );
};

export default JournalGlconfig;
